import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { useMemo } from "react";
import { FormattedMessage } from "react-intl";

import PrimaryButton from "@components/buttons/PrimaryButton";
import SecondaryButton from "@components/buttons/SecondaryButton";
import styles from "@components/onboarding/OnboardingButtons.module.css";
import ButtonLink from "@components/typography/ButtonLink";
import InternalLink from "@components/typography/InternalLink";
import { clickCurrentTarget } from "@utils/keyboardUtils";

type FormState = {
  formState: {
    isSubmitting: boolean;
    isValid: boolean;
  };
  reset: () => void;
};

type Props = {
  finalStep?: boolean;
  hookForm: FormState;
  onPrevious: () => void;
  onSkip?: () => void;
  previousAllowed: boolean;
  skipAllowed: boolean;
};

const OnboardingButtons = ({
  finalStep = false,
  hookForm,
  onPrevious,
  onSkip,
  previousAllowed,
  skipAllowed
}: Props) => {
  const saveButtonDisabled = useMemo(
    () => !hookForm.formState.isValid || hookForm.formState.isSubmitting,
    [hookForm.formState.isSubmitting, hookForm.formState.isValid]
  );

  const prevDisabled = useMemo(() => hookForm?.formState.isSubmitting, [hookForm?.formState.isSubmitting]);

  return (
    <div className={styles.fixedButtons}>
      <IonGrid className={styles.fullHeight}>
        <IonRow className="ion-align-items-center ion-justify-content-center">
          <IonCol size="auto">
            <InternalLink to="/sign-out">
              <FormattedMessage id="menus.client.signOut" />
            </InternalLink>
          </IonCol>
          <IonCol className="ion-text-center">
            {previousAllowed && (
              <>
                <span className="ion-hide-sm-down">
                  <SecondaryButton
                    data-test="onboarding-prev"
                    disabled={prevDisabled}
                    onClick={onPrevious}
                    textKey="dictionary.previous"
                  />
                </span>
                <span className="ion-hide-sm-up">
                  <SecondaryButton disabled={prevDisabled} icon="arrowLeft" onClick={onPrevious} />
                </span>
              </>
            )}
            <>
              <span className="ion-hide-sm-down">
                <PrimaryButton
                  data-test="onboarding-next"
                  disabled={saveButtonDisabled}
                  onKeyUp={clickCurrentTarget}
                  tabIndex={0}
                  textKey={finalStep ? "dictionary.complete" : "dictionary.next"}
                  type="submit"
                />
              </span>
              <span className="ion-hide-sm-up">
                <PrimaryButton
                  disabled={saveButtonDisabled}
                  icon={finalStep ? "checkmark" : "arrowRight"}
                  onKeyUp={clickCurrentTarget}
                  tabIndex={0}
                  type="submit"
                />
              </span>
            </>
          </IonCol>
          <IonCol size="auto">
            {skipAllowed && onSkip && (
              <ButtonLink onClick={onSkip}>
                <FormattedMessage id="dictionary.skip" />
              </ButtonLink>
            )}
          </IonCol>
        </IonRow>
      </IonGrid>
    </div>
  );
};

export default OnboardingButtons;
